import { UsersApi } from "@/api/users";
import { useUIStore } from "@/stores";
import { formatUserName } from "@/utils";
import { User } from "@/types";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
} from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import { useAddAffiliate } from "@/features/users/queries";
import debounce from "lodash.debounce";
import { useQuery } from "@tanstack/react-query";
import { QueryKey } from "@/utils";

const AddAffiliateUser: FC<{  affiliateId: string }> = ({ affiliateId }) => {
  const { closeModal } = useUIStore();
  const { addAffiliate, isAddAffiliateLoading } = useAddAffiliate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const debouncedSetSearch = useCallback(
    debounce((query: string) => {
      setDebouncedSearch(query);
    }, 300),
    []
  );

  useEffect(() => {
    debouncedSetSearch(searchQuery);
  }, [searchQuery, debouncedSetSearch]);

  const { data: users, isLoading: isUsersLoading } = useQuery({
    queryKey: [QueryKey.USERS, "search", debouncedSearch],
    queryFn: () =>
      UsersApi.getUsers({
        search: debouncedSearch || undefined,
        page: 1,
        perPage: 20,
        skipStatusFilter: true
      }),
    enabled: debouncedSearch.length > 0,
  });

  const handleAddAffiliateUser = () => {
    if (selectedUser) {
      addAffiliate({ userId: selectedUser.id, affiliateId });
    }
  };

  return (
    <Box>
      <Stack spacing={2}>
        <Autocomplete
          options={users?.data || []}
          loading={isUsersLoading}
          getOptionLabel={(option) =>
            `${formatUserName(option.firstName, option.lastName, option.status)} (${
              option.email
            })`
          }
          value={selectedUser}
          onChange={(_, newValue) => setSelectedUser(newValue)}
          onInputChange={(_, newInputValue) => setSearchQuery(newInputValue)}
          inputValue={searchQuery}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search users"
              placeholder="Type to search..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isUsersLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={
            debouncedSearch.length === 0
              ? "Start typing to search users"
              : "No users found"
          }
          sx={{ width: "100%" }}
        />

        <Stack direction="row" spacing={2} mt={2}>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            onClick={closeModal}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            onClick={handleAddAffiliateUser}
            loading={isAddAffiliateLoading}
            disabled={!selectedUser || isAddAffiliateLoading}
          >
            Add User
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export { AddAffiliateUser };

